"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Cpu, Lightbulb, Code as Code2, Bug, GitFork, Users, Layers, ShieldCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
interface PillarItem {
  number: string;
  title: string;
  vi: string;
  en: string;
  icon: LucideIcon;
}
const PILLARS: PillarItem[] = [{
  number: "01",
  title: "PROGRESSION",
  vi: "Học theo lộ trình thay vì các lớp học rời rạc.",
  en: "Structured learning pathways rather than isolated individual classes.",
  icon: TrendingUp
}, {
  number: "02",
  title: "HANDS-ON",
  vi: "Học viên trực tiếp làm việc với thiết bị.",
  en: "Students engage directly with authentic physical hardware.",
  icon: Cpu
}, {
  number: "03",
  title: "ENGINEERING THINKING",
  vi: "Không chỉ học cách sử dụng robot mà hiểu cách giải quyết vấn đề.",
  en: "Moving beyond operating robots to mastering core engineering problem-solving.",
  icon: Lightbulb
}, {
  number: "04",
  title: "PROGRAMMING",
  vi: "Từng bước phát triển từ tư duy trực quan đến lập trình.",
  en: "Step-by-step evolution from visual logic to code programming.",
  icon: Code2
}, {
  number: "05",
  title: "DEBUGGING",
  vi: "Học viên được thử nghiệm, phát hiện lỗi và cải tiến.",
  en: "Empowering students to test, detect bugs, and continuously iterate.",
  icon: Bug
}, {
  number: "06",
  title: "MULTIPLE PATHWAYS",
  vi: "Sau core pathway, học viên có thể tiếp tục theo các hướng Coding, Engineering, Competition, STEM/STEAM hoặc các chương trình chuyên biệt khác tùy mục tiêu.",
  en: "After the core pathway, students expand into Coding, Engineering, Competition, STEM/STEAM, or specialized tracks tailored to their goals.",
  icon: GitFork
}];
export default function RobotoDifferentiators() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const stored = localStorage.getItem("roboto_lang");
    if (stored === "en" || stored === "vi") {
      setLang(stored);
    }
    const handleLangChange = (e: Event) => {
      const customEv = e as CustomEvent<string>;
      if (customEv.detail && (customEv.detail === "en" || customEv.detail === "vi")) {
        setLang(customEv.detail);
      } else {
        const updated = localStorage.getItem("roboto_lang");
        if (updated === "en" || updated === "vi") {
          setLang(updated);
        }
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="robotodifferentiators" data-nav="light" className="relative py-16 md:py-24 bg-background text-foreground overflow-hidden">
      {/* Decorative background grid pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:16px_16px]" aria-hidden="true" />

      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Block */}
        <div className="max-w-3xl mb-10 md:mb-12">
          {/* Precision Spec Rail */}
          <div className="flex items-center gap-3 mb-4">
            <div className="h-[1px] w-12 bg-secondary" aria-hidden="true" />
            <span className="text-xs font-mono tracking-wider text-secondary font-semibold uppercase">
              {lang === "vi" ? "Đặc Điểm Nổi Bật" : "Key Differentiators"}
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-foreground tracking-tight leading-tight">
            {lang === "vi" ? "ĐIỀU GÌ TẠO NÊN KHÁC BIỆT CỦA ROBOTO?" : "WHAT MAKES ROBOTO DIFFERENT?"}
          </h2>
        </div>

        {/* Hero Visual Diagram: Teaching Differentiators & Mentor Matrix */}
        <div className="mb-12 md:mb-16 rounded-2xl bg-[#0a0f1d] border border-border/80 p-6 sm:p-8 relative overflow-hidden shadow-2xl text-slate-100">
          {/* Blueprint grid pattern overlay */}
          <div className="absolute inset-0 pointer-events-none opacity-20 bg-[linear-gradient(to_right,#38bdf815_1px,transparent_1px),linear-gradient(to_bottom,#38bdf815_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          
          {/* Ambient glow effects */}
          <div className="absolute -top-24 -left-24 size-96 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 size-96 bg-secondary/15 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Info & Key Ratios */}
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/10 border border-secondary/30 text-secondary text-xs font-mono font-bold tracking-wider uppercase">
                <ShieldCheck className="size-3.5" />
                {lang === "vi" ? "Tiêu Chuẩn Giảng Dạy Premium" : "Premium Teaching Standards"}
              </div>

              <div>
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-white tracking-tight mb-2">
                  {lang === "vi" ? "Mô Hình Tương Tác & Lộ Trình Chuẩn Hóa" : "Mentorship Ratio & Structured Standards"}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {lang === "vi" ? "ROBOTO áp dụng tỷ lệ hướng dẫn tối ưu kết hợp với sơ đồ phát triển năng lực định lượng, giúp từng học viên làm chủ phần cứng và tư duy lập trình thực tế." : "ROBOTO integrates optimal mentorship density with quantified technical progression paths, ensuring every student masters physical hardware and computational logic."}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 backdrop-blur-sm">
                  <div className="text-2xl sm:text-3xl font-mono font-bold text-secondary mb-1">
                    1 : 4
                  </div>
                  <div className="text-xs font-semibold text-slate-200">
                    {lang === "vi" ? "Tỷ lệ Mentor / Học viên" : "Mentor-to-Student Ratio"}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    {lang === "vi" ? "Tối đa 6 học viên / lớp" : "Max 6 students per session"}
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 backdrop-blur-sm">
                  <div className="text-2xl sm:text-3xl font-mono font-bold text-amber-400 mb-1">
                    100%
                  </div>
                  <div className="text-xs font-semibold text-slate-200">
                    {lang === "vi" ? "Lộ trình liên tục" : "Structured Progression"}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    {lang === "vi" ? "Từ trực quan đến Python/C++" : "Visual to Python/C++"}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Technical Diagrams */}
            <div className="lg:col-span-7 space-y-6">
              {/* Diagram 1: Mentor-to-Student Interactive Node Schematic */}
              <div className="p-5 rounded-xl bg-slate-900/90 border border-slate-800">
                <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-3">
                  <div className="flex items-center gap-2 text-xs font-mono font-semibold text-slate-300">
                    <Users className="size-4 text-secondary" />
                    <span>{lang === "vi" ? "SƠ ĐỒ TƯƠNG TÁC MENTOR & LAB" : "MENTOR-STUDENT INTERACTION MATRIX"}</span>
                  </div>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-secondary/20 text-secondary border border-secondary/40 font-bold">
                    OPTIMAL FEEDBACK
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-center text-center">
                  {/* Central Mentor Node */}
                  <div className="sm:col-span-1 flex flex-col items-center justify-center p-3 rounded-lg bg-primary/20 border border-primary/40 text-white">
                    <div className="size-10 rounded-full bg-primary flex items-center justify-center mb-1 text-white shadow-lg shadow-primary/30">
                      <Users className="size-5" />
                    </div>
                    <span className="text-xs font-bold font-mono">MENTOR</span>
                    <span className="text-[10px] text-slate-300">{lang === "vi" ? "Chuyên Gia" : "Certified Lead"}</span>
                  </div>

                  {/* Connected Student Workstation Terminals */}
                  <div className="sm:col-span-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[{
                    id: "LAB-01",
                    status: lang === "vi" ? "Lắp Ráp Hardware" : "Assembly",
                    icon: Cpu
                  }, {
                    id: "LAB-02",
                    status: lang === "vi" ? "Lập Trình Logic" : "Logic Flow",
                    icon: Code2
                  }, {
                    id: "LAB-03",
                    status: lang === "vi" ? "Debug Mã Nguồn" : "Code Debug",
                    icon: Bug
                  }, {
                    id: "LAB-04",
                    status: lang === "vi" ? "Thử Nghiệm Mạch" : "Circuit Test",
                    icon: Lightbulb
                  }].map(station => {
                    const StIcon = station.icon;
                    return <div key={station.id} className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 text-center relative group hover:border-secondary/50 transition-colors">
                          <div className="absolute top-1 right-1 size-1.5 rounded-full bg-secondary animate-pulse" />
                          <StIcon className="size-4 text-secondary mx-auto mb-1" />
                          <div className="text-[11px] font-mono font-bold text-slate-200">{station.id}</div>
                          <div className="text-[10px] text-slate-400 truncate">{station.status}</div>
                        </div>;
                  })}
                  </div>
                </div>
              </div>

              {/* Diagram 2: Structured Pathway Pipeline */}
              <div className="p-5 rounded-xl bg-slate-900/90 border border-slate-800">
                <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-3">
                  <div className="flex items-center gap-2 text-xs font-mono font-semibold text-slate-300">
                    <Layers className="size-4 text-amber-400" />
                    <span>{lang === "vi" ? "TIẾN TRÌNH PHÁT TRIỂN TIÊU CHUẨN" : "STRUCTURED PROGRESSION PIPELINE"}</span>
                  </div>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-400/10 text-amber-400 border border-amber-400/30 font-bold">
                    ZERO DEAD-END
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-left">
                  {[{
                  stage: "STAGE 01",
                  title: lang === "vi" ? "Khám Phá" : "Foundations",
                  desc: "4 - 6 YRS",
                  color: "border-secondary/40 text-secondary"
                }, {
                  stage: "STAGE 02",
                  title: lang === "vi" ? "Xây Dựng" : "Mechanics",
                  desc: "7 - 9 YRS",
                  color: "border-sky-400/40 text-sky-400"
                }, {
                  stage: "STAGE 03",
                  title: lang === "vi" ? "Lập Trình" : "Algorithm",
                  desc: "10 - 12 YRS",
                  color: "border-amber-400/40 text-amber-400"
                }, {
                  stage: "STAGE 04",
                  title: lang === "vi" ? "Thi Đấu" : "Mastery",
                  desc: "13 - 16 YRS",
                  color: "border-primary/40 text-primary"
                }].map(st => <div key={st.stage} className={`p-3 rounded-lg bg-slate-950/90 border ${st.color} relative`}>
                      <div className="text-[10px] font-mono font-bold tracking-widest opacity-80 mb-0.5">{st.stage}</div>
                      <div className="text-xs font-bold text-white mb-0.5">{st.title}</div>
                      <div className="text-[10px] text-slate-400">{st.desc}</div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 6-Pillar Card Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {PILLARS.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.number} variants={cardVariants} data-index={i}>
                <Card className="h-full border border-border bg-card hover:border-secondary/50 hover:shadow-md transition-all duration-300 group">
                  <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
                    <span className="text-2xl font-mono font-bold text-secondary tracking-wider">
                      {pillar.number}
                    </span>
                    <div className="p-2 rounded-lg bg-muted/60 text-muted-foreground group-hover:bg-secondary/10 group-hover:text-secondary transition-colors duration-300">
                      <Icon className="size-5" />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <CardTitle className="text-lg font-bold font-sans text-card-foreground tracking-tight">
                      {pillar.title}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {lang === "vi" ? pillar.vi : pillar.en}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}